import { Avatar, Divider } from '@mui/material';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNotification } from 'src/context/NotificationContext';
import Messages from '../../config/messages.constants';
import useCountdown from '../../hooks/useCountdown';
import Button from '../common/Button';
// import RingingIcon from '../common/Icons/Ringing';
import TextButton from '../common/TextButton';
import AnimatedGif from '../../assets/images/phone-ringing.gif';

import { useCall, useTwilioUserMediaContext } from '../../customer-call-sdk';
import Connecting from '../Connecting';

const StyledIncomingCallJoin = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    padding: 1.5rem;
    border-radius: 0.625rem;
    overflow-y: auto;

    animation-name: blink;
    animation-duration: 1s;
    animation-iteration-count: infinite;

    @keyframes blink {
        0% {
            background-color: #fff;
        }
        50% {
            background-color: #e3e989;
        }
        80% {
            background-color: #fff;
        }
    }

    .header,
    .footer {
        width: 100%;
        display: flex;
        flex-direction: column;
    }
    .heading {
        color: var(--text-primary);
        font-size: 2.125rem;
        font-weight: bold;
        line-height: 2.5rem;
        text-align: center;
        margin-bottom: 1rem;
    }

    .caller-info {
        color: var(--text-primary);
        font-size: 1.75rem;
        font-weight: bold;
        margin-bottom: 0.5rem;
        text-align: center;
        text-transform: capitalize;
    }

    .divider {
        width: 100%;
        margin: 0 -1.5rem 1rem;
        padding: 0 1.5rem;
    }

    .interpreter-container {
        margin-top: 0.5rem;
        margin-bottom: 1.5rem;
    }

    .interpreter-label {
        color: rgba(17, 17, 17, 0.6);
        font-size: 1.2rem;
        font-weight: 600;
        margin-bottom: 0.5rem;
        text-align: center;
    }

    .interpreter-info {
        color: var(--text-primary);
        font-size: 1.75rem;
        font-weight: bold;
        margin-bottom: 0.5rem;
        text-align: center;
        text-transform: capitalize;
    }

    .avatar-container {
        margin-top: 2.5rem;
        margin-bottom: 1.5rem;
        display: flex;
        justify-content: center;
        .avatar {
            width: 170px;
            height: 170px;
            background-color: #fff;
            border: 2px solid #111111;
        }
        .avatar svg {
            width: 70px;
            height: 70px;
        }
        .avatar img {
            width: 120px;
            height: 120px;
        }
    }
    .media-error {
        font-size: 1rem;
        text-align: center;
        color: rgb(255, 0, 0);
        margin-bottom: 1rem;
    }

    .auto-joining-text {
        color: var(--text-primary);
        font-weight: bold;
        margin-bottom: 0.5rem;
        text-align: center;
    }

    .answer-btn {
        margin-bottom: 0.5rem;
    }
`;

export interface IncomingCallJoinProps extends React.HTMLAttributes<HTMLDivElement> {}

const IncomingCallJoin: React.FC<IncomingCallJoinProps> = ({ ...props }) => {
    const call = useCall();
    const { showNotification, closeNotifications } = useNotification();
    const { callerName, interpreterName, callerNumber } = call;
    const { isMediaPermissionDenied } = useTwilioUserMediaContext();
    const { countdown, startTimer, stopTimer } = useCountdown(45);
    const [countdownVisible, setCountdownVisible] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const isInterpretrerAvailable = !!interpreterName;
    const handleAnswerClick = async () => {
        setIsLoading(true);
        await call.answer();
        setIsLoading(false);
    };

    const handleDeclineClick = async () => {
        setIsLoading(true);
        await call.reject();
        setIsLoading(false);
    };

    useEffect(() => {
        startTimer();
        showNotification('Incoming call', callerName || callerNumber || '', true);
        return () => {
            stopTimer();
            closeNotifications();
        };
    }, []);

    useEffect(() => {
        if (countdown === 5) {
            setCountdownVisible(true);
        } else if (countdown === 0) {
            call.closeAnswer();
        }
    }, [countdown]);

    return (
        <StyledIncomingCallJoin {...props}>
            <div className="header">
                <h3 className="heading">Incoming call...</h3>

                <div className="avatar-container">
                    <Avatar className="avatar">
                        {/* <RingingIcon /> */}
                        <img src={AnimatedGif} width="70px" height="70px" alt="" />
                    </Avatar>
                </div>
                <div className="caller-info">{callerName || callerNumber}</div>
                {isInterpretrerAvailable && (
                    <>
                        <Divider className="divider" />
                        <div className="interpreter-container">
                            <p className="interpreter-label">Your video interpreter</p>
                            <p className="interpreter-info">{interpreterName}</p>
                        </div>
                    </>
                )}
            </div>
            <div className="footer">
                {isMediaPermissionDenied && (
                    <div className="media-error">{Messages.INCOMING_CALL_CAMERA_NOT_CONNECTED}</div>
                )}
                {countdownVisible && (
                    <div className="auto-joining-text">{`Auto-closing in ${countdown} sec...`}</div>
                )}
                <Button
                    fullWidth
                    size="large"
                    className="answer-btn capitalize"
                    onClick={handleAnswerClick}
                    disabled={isLoading || isMediaPermissionDenied}
                >
                    {isLoading ? <Connecting id="incoming-call-answer-loading" /> : 'Answer'}
                </Button>
                <TextButton
                    color="secondary"
                    onClick={handleDeclineClick}
                    className="capitalize"
                    disabled={isLoading}
                >
                    Decline
                </TextButton>
            </div>
        </StyledIncomingCallJoin>
    );
};

export default IncomingCallJoin;
